<template>
  <div class="channel-members-page">
    <!-- 头部 -->
    <div class="members-header">
      <van-icon name="arrow-left" size="20" color="#333" style="cursor: pointer;" @click="$router.back()" />
      <span class="members-title">{{ $t('channel_members_title') }}</span>
      <span></span>
    </div>

    <!-- 加载中 -->
    <div v-if="loading && members.length === 0" style="display: flex; justify-content: center; padding: 40px;">
      <van-loading size="24" />
    </div>

    <!-- 空状态 -->
    <van-empty v-else-if="members.length === 0" :description="$t('channel_members_empty')" />

    <!-- 成员网格 -->
    <div v-else class="member-grid" ref="memberGrid" @scroll="onScroll">
      <div
        v-for="(member, index) in members"
        :key="index"
        class="member-cell"
        @click="showUserDetail(member)"
      >
        <!-- 头像 -->
        <img
          v-if="member.avatar"
          :src="$common.getFullImageUrl(member.avatar, $config, 's')"
          class="member-avatar"
        />
        <div v-else class="member-avatar-placeholder">
          {{ (member.userName || '?')[0] }}
        </div>

        <!-- 昵称 -->
        <div class="member-name" :title="member.userName">
          {{ member.userName || $t('anonymous') }}
        </div>
      </div>

      <!-- 加载更多 -->
      <div v-if="loading && hasMore" class="member-cell loading-cell">
        <van-loading size="20" />
      </div>
    </div>

    <!-- 用户详情弹窗 -->
    <ContactMethods
      :visible.sync="showUserDetailPopup"
      :targetUserId="selectedUserId"
      :userName="selectedUserName"
      :userAvatar="selectedUserAvatar"
      :userBio="selectedUserBio"
      :showBlockActions="false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Loading, Empty } from 'vant'
import ContactMethods from './ContactMethods.vue'

Vue.use(Icon)
Vue.use(Loading)
Vue.use(Empty)

export default {
  name: 'ChannelMembers',
  components: {
    ContactMethods
  },
  data() {
    return {
      channelId: '',
      members: [],
      loading: false,
      hasMore: true,
      curPage: 1,
      pageSize: 50,
      // 用户详情弹窗
      showUserDetailPopup: false,
      selectedUserId: '',
      selectedUserName: '',
      selectedUserAvatar: '',
      selectedUserBio: ''
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.channelId = String(newVal)
          this.loadMembers()
        }
      }
    }
  },
  methods: {
    async loadMembers() {
      if (this.loading) return
      this.loading = true
      this.curPage = 1
      try {
        const response = await axios.post('/api/channel/members', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          curPage: 1
        })
        if (response.data.code === 0 && response.data.data) {
          this.members = response.data.data.members || []
          this.hasMore = this.members.length >= this.pageSize
        } else {
          this.members = []
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelMembers] loadMembers error:', error)
        this.members = []
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (this.loading || !this.hasMore) return
      this.loading = true
      this.curPage++
      try {
        const response = await axios.post('/api/channel/members', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          curPage: this.curPage
        })
        if (response.data.code === 0 && response.data.data) {
          const newMembers = response.data.data.members || []
          this.members = this.members.concat(newMembers)
          this.hasMore = newMembers.length >= this.pageSize
        } else {
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelMembers] loadMore error:', error)
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    onScroll() {
      const el = this.$refs.memberGrid
      if (!el || this.loading || !this.hasMore) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadMore()
      }
    },
    showUserDetail(member) {
      this.selectedUserId = String(member.userId || '')
      this.selectedUserName = member.userName || ''
      this.selectedUserAvatar = member.avatar || ''
      this.selectedUserBio = ''
      this.showUserDetailPopup = true
      this.fetchBio(String(member.userId || ''))
    },
    async fetchBio(userId) {
      try {
        const response = await axios.post('/api/user/get_basic_user_info', {
          targetUserId: userId
        })
        if (response.data && response.data.code === 0 && response.data.data) {
          this.selectedUserBio = response.data.data.bio || ''
        }
      } catch (error) {
        console.error('[ChannelMembers] Failed to load user bio:', error)
      }
    }
  }
}
</script>

<style scoped>
.channel-members-page {
  background: #F7F5F2;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.members-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.members-title {
  font-size: 16px;
  font-weight: 500;
  color: #1A1A1A;
}

.member-grid {
  flex: 1;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px 12px;
  padding: 12px;
  align-content: start;
}

.member-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
}

.member-cell:active {
  opacity: 0.7;
}

.loading-cell {
  justify-content: center;
  min-height: 80px;
}

.member-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16%;
  object-fit: cover;
}

.member-avatar-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 16%;
  background: #E8543A;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.member-name {
  margin-top: 4px;
  font-size: 11px;
  color: #1A1A1A;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelMembers.vue